import { useState } from 'react'
import axios from 'axios'
import { submitFeedback } from '@/api/feedbackApi'
import { enqueueFeedback } from '@/db/viewerDb'
import { useAppStore } from '@/store/appStore'
import type { FeedbackContext, FeedbackSubmission, FeedbackType } from '@/types/api'

export type FeedbackSubmitOutcome = 'submitted' | 'queued'

/** A failure the server owns (disabled feature, validation). Never queued for retry. */
function isRejection(err: unknown): boolean {
  return axios.isAxiosError(err) && err.response !== undefined && err.response.status < 500
}

/**
 * Submits feedback, or queues it in IndexedDB when the user is offline so `useReconnectSync`
 * can flush it once connectivity returns.
 */
export function useSubmitFeedback() {
  const isOffline = useAppStore((s) => s.isOffline)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const submit = async (
    type: FeedbackType,
    comment: string,
    context: FeedbackContext
  ): Promise<FeedbackSubmitOutcome> => {
    const submission: FeedbackSubmission = {
      clientId: crypto.randomUUID(),
      type,
      comment,
      submittedOn: new Date().toISOString(),
      context,
    }

    if (isOffline) {
      await enqueueFeedback(submission)
      return 'queued'
    }

    setIsSubmitting(true)
    try {
      await submitFeedback([submission])
      return 'submitted'
    } catch (err) {
      // A server-side rejection is final, so surface it. Anything else (dropped connection,
      // upstream outage) is queued so the entry is not lost.
      if (isRejection(err)) throw err
      await enqueueFeedback({ ...submission, context: { ...context, wasOffline: true } })
      return 'queued'
    } finally {
      setIsSubmitting(false)
    }
  }

  return { submit, isSubmitting }
}
